import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Users,
  User,
  Plus,
  Search,
  MoreVertical,
  Edit,
  Trash2,
  History,
  Calendar,
  Mail,
  Phone
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { useAuth } from '../context/SupabaseAuthContext';
import {
  archivePatient,
  createPatient,
  listPatients,
  updatePatient,
} from '../lib/patients';

const SEX_OPTIONS = [
  { value: 'female', label: 'Female' },
  { value: 'male', label: 'Male' },
  { value: 'intersex', label: 'Intersex' },
  { value: 'unknown', label: 'Unknown' },
  { value: 'not_recorded', label: 'Not recorded' },
];
const SEX_LABELS = Object.fromEntries(SEX_OPTIONS.map((o) => [o.value, o.label]));

export const PatientsPage = () => {
  const { organization, user, isDemo } = useAuth();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [patientIdNumber, setPatientIdNumber] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [sex, setSex] = useState('not_recorded');
  const [submitting, setSubmitting] = useState(false);

  const fetchPatients = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listPatients(organization.id);
      setPatients(data);
    } catch (error) {
      console.error('Failed to fetch patients:', error);
      toast.error(error.message || 'Failed to load patients');
    } finally {
      setLoading(false);
    }
  }, [organization.id]);

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  const handleAddPatient = async () => {
    if (!firstName || !lastName || !dateOfBirth) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const newPatient = await createPatient(organization.id, user.user_id, {
        first_name: firstName,
        last_name: lastName,
        date_of_birth: dateOfBirth,
        patient_id_number: patientIdNumber,
        email,
        phone,
        sex,
      });
      setPatients((current) => [...current, newPatient].sort((a, b) =>
        `${a.last_name}${a.first_name}`.localeCompare(`${b.last_name}${b.first_name}`)
      ));
      setIsAddDialogOpen(false);
      resetForm();
      toast.success('Patient added successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to add patient');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditPatient = async () => {
    if (!selectedPatient) return;

    setSubmitting(true);
    try {
      const updatedPatient = await updatePatient(
        organization.id,
        selectedPatient.patient_id,
        {
          first_name: firstName,
          last_name: lastName,
          date_of_birth: dateOfBirth,
          patient_id_number: patientIdNumber,
          email,
          phone,
          sex,
        }
      );
      setPatients((current) => current.map((patient) =>
        patient.patient_id === selectedPatient.patient_id ? updatedPatient : patient
      ));
      setIsEditDialogOpen(false);
      resetForm();
      toast.success('Patient updated successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to update patient');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePatient = async (patientId) => {
    try {
      await archivePatient(organization.id, patientId);
      setPatients((current) => current.filter((patient) => patient.patient_id !== patientId));
      toast.success('Patient archived successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to archive patient');
    }
    setDeleteConfirmId(null);
  };

  const openEditDialog = (patient) => {
    setSelectedPatient(patient);
    setFirstName(patient.first_name);
    setLastName(patient.last_name);
    setDateOfBirth(patient.date_of_birth);
    setPatientIdNumber(patient.patient_id_number);
    setEmail(patient.email || '');
    setPhone(patient.phone || '');
    setSex(patient.sex_at_birth || 'not_recorded');
    setIsEditDialogOpen(true);
  };

  const resetForm = () => {
    setFirstName('');
    setLastName('');
    setDateOfBirth('');
    setPatientIdNumber('');
    setEmail('');
    setPhone('');
    setSex('not_recorded');
    setSelectedPatient(null);
  };

  const filteredPatients = patients.filter(patient => {
    const searchLower = searchQuery.toLowerCase();
    return (
      patient.first_name.toLowerCase().includes(searchLower) ||
      patient.last_name.toLowerCase().includes(searchLower) ||
      patient.patient_id_number.toLowerCase().includes(searchLower)
    );
  });

  const formatDate = (dateStr) => {
    try {
      if (!dateStr) return '';
      const datePart = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
      const [year, month, day] = datePart.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      return format(date, 'MMM dd, yyyy');
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="space-y-6" data-testid="patients-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Patients</h1>
          <p className="text-slate-600 mt-1">Manage your patient records.</p>
        </div>
        <Dialog open={isAddDialogOpen} onOpenChange={(open) => {
          setIsAddDialogOpen(open);
          if (!open) resetForm();
        }}>
          {!isDemo && (
            <DialogTrigger asChild>
              <Button
                className="bg-[#196a70] hover:bg-[#12565b] text-white"
                data-testid="add-patient-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Patient
              </Button>
            </DialogTrigger>
          )}
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="font-heading">Add New Patient</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="add-first-name">First Name *</Label>
                  <Input
                    id="add-first-name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="John"
                    data-testid="patient-first-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="add-last-name">Last Name *</Label>
                  <Input
                    id="add-last-name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Doe"
                    data-testid="patient-last-name"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="add-dob">Date of Birth *</Label>
                  <Input
                    id="add-dob"
                    type="date"
                    value={dateOfBirth}
                    onChange={(e) => setDateOfBirth(e.target.value)}
                    data-testid="patient-dob"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="add-patient-id">Patient ID</Label>
                  <Input
                    id="add-patient-id"
                    value={patientIdNumber}
                    onChange={(e) => setPatientIdNumber(e.target.value)}
                    placeholder="Auto-generated if blank"
                    className="font-mono"
                    data-testid="patient-id-number"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="add-sex">Sex</Label>
                <Select value={sex} onValueChange={setSex}>
                  <SelectTrigger id="add-sex" data-testid="patient-sex">
                    <SelectValue placeholder="Select sex" />
                  </SelectTrigger>
                  <SelectContent>
                    {SEX_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="add-email">Email</Label>
                  <Input
                    id="add-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="john@example.com"
                    data-testid="patient-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="add-phone">Phone</Label>
                  <Input
                    id="add-phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+1 (555) 123-4567"
                    data-testid="patient-phone"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  onClick={handleAddPatient}
                  disabled={submitting}
                  className="bg-[#196a70] hover:bg-[#12565b] text-white"
                  data-testid="submit-patient-btn"
                >
                  {submitting ? 'Saving...' : 'Add Patient'}
                </Button>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <Card className="bg-white border-slate-200">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search patients by name or ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-patients"
            />
          </div>
        </CardContent>
      </Card>

      {/* Patients Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Card key={i} className="bg-white border-slate-200">
              <CardContent className="p-6">
                <div className="space-y-4">
                  <Skeleton className="h-6 w-2/3" />
                  <Skeleton className="h-4 w-1/2" />
                  <Skeleton className="h-4 w-1/3" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredPatients.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPatients.map((patient) => (
            <Card
              key={patient.patient_id}
              className="bg-white border-slate-200 shadow-soft card-hover"
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#dbe9e5] flex items-center justify-center">
                      <Users className="w-5 h-5 text-[#196a70]" />
                    </div>
                    <div>
                      <h3 className="font-heading font-semibold text-slate-900">
                        {patient.first_name} {patient.last_name}
                      </h3>
                      <p className="text-sm text-slate-500 font-mono">
                        {patient.patient_id_number}
                      </p>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        data-testid={`patient-menu-${patient.patient_id}`}
                      >
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {!isDemo && (
                        <DropdownMenuItem onClick={() => openEditDialog(patient)}>
                          <Edit className="w-4 h-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem asChild>
                        <Link to={`/patients/${patient.patient_id}/history`}>
                          <History className="w-4 h-4 mr-2" />
                          View History
                        </Link>
                      </DropdownMenuItem>
                      {!isDemo && (
                        <DropdownMenuItem
                          onClick={() => setDeleteConfirmId(patient.patient_id)}
                          className="text-red-600"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Archive
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-slate-600">
                    <Calendar className="w-4 h-4 text-slate-400" />
                    <span>DOB: {formatDate(patient.date_of_birth)}</span>
                  </div>
                  {patient.sex_at_birth && patient.sex_at_birth !== 'not_recorded' && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <User className="w-4 h-4 text-slate-400" />
                      <span>{SEX_LABELS[patient.sex_at_birth] || patient.sex_at_birth}</span>
                    </div>
                  )}
                  {patient.email && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <Mail className="w-4 h-4 text-slate-400" />
                      <span className="truncate">{patient.email}</span>
                    </div>
                  )}
                  {patient.phone && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <Phone className="w-4 h-4 text-slate-400" />
                      <span>{patient.phone}</span>
                    </div>
                  )}
                </div>

                <div className="mt-4 pt-4 border-t border-slate-100 flex gap-2">
                  <Link to={`/patients/${patient.patient_id}/history`} className="flex-1">
                    <Button
                      variant="default"
                      size="sm"
                      className="w-full bg-[#196a70] hover:bg-[#004d87]"
                      data-testid={`view-analyses-${patient.patient_id}`}
                    >
                      <History className="w-4 h-4 mr-2" />
                      View Analyses
                    </Button>
                  </Link>
                  {!isDemo && (
                    <Link to={`/upload?patient=${patient.patient_id}`}>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-[#196a70] border-[#196a70]/30 hover:bg-[#dbe9e5]"
                        data-testid={`upload-for-${patient.patient_id}`}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </Link>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="bg-white border-slate-200">
          <CardContent className="p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="font-heading font-medium text-slate-900 mb-1">
              {searchQuery ? 'No patients found' : 'No patients yet'}
            </h3>
            <p className="text-slate-500 text-sm mb-4">
              {searchQuery
                ? 'Try adjusting your search query.'
                : 'Add your first patient to get started.'}
            </p>
            {!searchQuery && !isDemo && (
              <Button
                onClick={() => setIsAddDialogOpen(true)}
                className="bg-[#196a70] hover:bg-[#12565b] text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Patient
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={(open) => {
        setIsEditDialogOpen(open);
        if (!open) resetForm();
      }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Patient</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-first-name">First Name *</Label>
                <Input
                  id="edit-first-name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="John"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-last-name">Last Name *</Label>
                <Input
                  id="edit-last-name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Doe"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-dob">Date of Birth *</Label>
                <Input
                  id="edit-dob"
                  type="date"
                  value={dateOfBirth}
                  onChange={(e) => setDateOfBirth(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-patient-id">Patient ID</Label>
                <Input
                  id="edit-patient-id"
                  value={patientIdNumber}
                  onChange={(e) => setPatientIdNumber(e.target.value)}
                  placeholder="PAT-001"
                  className="font-mono"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-sex">Sex</Label>
              <Select value={sex} onValueChange={setSex}>
                <SelectTrigger id="edit-sex">
                  <SelectValue placeholder="Select sex" />
                </SelectTrigger>
                <SelectContent>
                  {SEX_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-email">Email</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="john@example.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-phone">Phone</Label>
                <Input
                  id="edit-phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+1 (555) 123-4567"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                onClick={handleEditPatient}
                disabled={submitting}
                className="bg-[#196a70] hover:bg-[#12565b] text-white"
              >
                {submitting ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirmId} onOpenChange={() => setDeleteConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive Patient</AlertDialogTitle>
            <AlertDialogDescription>
              Archive this patient? Their reports and assessment history will be preserved and hidden from the active patient list.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDeletePatient(deleteConfirmId)}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
